import React, { useEffect, useState } from 'react';

import { TextInput } from '@volkswagen-onehub/components-core';

import { Carlines, OptionsProps, Trims } from '../../typings/general';
import { fetchCarlines } from '../../fetch-data/fetch-carlines';
import { fetchTrims } from '../../fetch-data/fetch-trims';
import { SelectField } from '../selectField';
import {
  StyledContainer,
  StyledRowSelectContainer,
  StyledSelectContainer,
} from './styled';
import { useFeatureServices } from '../../hooks/use-feature-services';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import { useStore } from '../../hooks-store/store';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import IncentiveConstants from '../../hooks-store/incentive-store/actions/incentive-constants';

interface TabContentProps {
  vinApiError?: string | null;
  apiConfig: string;
  deeplinkModelYear?: string;
}

// TabSelector mounts both tab panels simultaneously (the underlying Tabs
// component only toggles `hidden`), so both TabContent instances would race
// to auto-fetch. This module-level guard ensures the deeplink fetch runs once.
let hasAutoFetchedDeeplink = false;

export const TabContent = ({
  vinApiError,
  apiConfig,
  deeplinkModelYear,
}: TabContentProps) => {
  const { translationHandler } = useMaintenanceContext();
  const [
    store,
    {
      [IncentiveConstants.UpdateStoreByFormInput]: updateStoreByFormInput,
      [IncentiveConstants.SetModelCardData]: updateModelCardData,
    },
  ] = useStore();
  const { form, formValidationError, modelYears, currentData } = store;
  const {
    vin: isVinInvalid,
    model: isModelInvalid,
    modelYear: isModelYearInvalid,
    trim: isTrimInvalid,
  } = formValidationError;
  const { 'service-config-provider': serviceProvider } = useFeatureServices();
  const { configs } = serviceProvider;
  const faServicesUrl = configs['fa-services']?.urlOrigin ?? '';
  const [vin, setVin] = useState(form.vin ?? '');
  const [carlines, setCarlines] = useState<OptionsProps[]>(
    currentData.carlines || [],
  );
  const [trims, setTrims] = useState<OptionsProps[]>(currentData.trims || []);

  const isVinTab = form.selectedTab === 'vin';
  const vinErrorMessage =
    isVinTab && vinApiError
      ? vinApiError
      : isVinInvalid
        ? translationHandler('form.vin.empty.error')
        : '';
  const vinAppearance = vinErrorMessage ? 'error' : 'default';

  const handleVinChange = (event) => {
    setVin(event.target.value);
    updateModelCardData({
      vin: event.target.value,
    });
  };

  const getCarlinesFromAPI = (value) => {
    fetchCarlines({
      faServicesUrl,
      apiKey: apiConfig,
      modelYear: value,
    }).then((result: Carlines) => {
      const { car_lines } = result;
      const formattedCarlines: OptionsProps[] = [];
      car_lines.forEach((carline) =>
        formattedCarlines.push({ value: carline.value, label: carline.value }),
      );
      setCarlines(formattedCarlines);
      updateModelCardData({
        modelYear: value,
        carlines: formattedCarlines,
      });
    });
  };

  const getTrimsFromAPI = (value: string) => {
    fetchTrims({
      faServicesUrl,
      apiKey: apiConfig,
      modelYear: form.modelYear ?? '',
      carline: value,
    }).then((result: Trims) => {
      const { model_trims } = result;
      const formattedModelTrims: OptionsProps[] = [];
      model_trims.forEach((trim) =>
        formattedModelTrims.push({
          value: trim.salesModelCode,
          label: trim.modelTrimDescription,
        }),
      );
      setTrims(formattedModelTrims);
      updateModelCardData({
        trims: formattedModelTrims,
        model: value,
      });
    });
  };

  const onSelectModelYear = (value) => {
    updateStoreByFormInput({
      ...form,
      modelYear: value,
      vin: undefined,
      model: undefined,
      trim: undefined,
    });

    getCarlinesFromAPI(value);
  };

  const onSelectCarline = (value) => {
    updateStoreByFormInput({
      ...form,
      model: value,
      vin: undefined,
      trim: undefined,
    });

    getTrimsFromAPI(value);
  };

  const onSelectTrim = (value: string) => {
    const selectedOption = trims.find((option) => option.value === value);

    updateStoreByFormInput({ ...form, trim: value, vin: undefined });

    updateModelCardData({
      trim: selectedOption?.label,
      vin: undefined,
    });
  };

  useEffect(() => {
    const shouldAutoFetch =
      !hasAutoFetchedDeeplink &&
      deeplinkModelYear &&
      form.modelYear === deeplinkModelYear &&
      carlines.length === 0 &&
      apiConfig;

    if (!shouldAutoFetch) {
      return;
    }
    hasAutoFetchedDeeplink = true;

    fetchCarlines({
      faServicesUrl,
      apiKey: apiConfig,
      modelYear: deeplinkModelYear,
    }).then((result: Carlines) => {
      const { car_lines } = result;
      const formattedCarlines: OptionsProps[] = car_lines.map((carline) => ({
        value: carline.value,
        label: carline.value,
      }));
      setCarlines(formattedCarlines);
      updateModelCardData({
        modelYear: deeplinkModelYear,
        carlines: formattedCarlines,
      });

      const matchedModel = formattedCarlines.some(
        (carline) => carline.value === form.model,
      );
      if (form.model && matchedModel) {
        getTrimsFromAPI(form.model);
      } else if (form.model) {
        updateStoreByFormInput({ ...form, model: undefined });
      }
    });
    // Guarded by the module-level hasAutoFetchedDeeplink flag against the
    // double-mount race; re-runs on apiConfig since it loads asynchronously
    // after mount and is empty on the first render.
  }, [apiConfig]);

  return (
    <StyledContainer className="tab-content">
      {form.selectedTab === 'modelDetails' ? (
        <StyledSelectContainer>
          <StyledRowSelectContainer>
            <SelectField
              label={translationHandler('form.model.year.placeholder')}
              options={modelYears}
              onChange={(value) => onSelectModelYear(value)}
              message={
                isModelYearInvalid
                  ? translationHandler('form.model.year.error')
                  : undefined
              }
              appearance={isModelYearInvalid ? 'error' : 'default'}
              value={form.modelYear ?? ''}
              disabled={!modelYears.length}
            />
            <SelectField
              label={translationHandler('form.model.placeholder')}
              options={carlines}
              onChange={(value) => onSelectCarline(value)}
              message={
                isModelInvalid
                  ? translationHandler('form.model.error')
                  : undefined
              }
              appearance={isModelInvalid ? 'error' : 'default'}
              value={form.model ?? ''}
              disabled={!form.modelYear || !carlines.length}
            />
          </StyledRowSelectContainer>
          <SelectField
            label={translationHandler('form.trim.placeholder')}
            options={trims}
            onChange={(value) => onSelectTrim(value)}
            message={
              isTrimInvalid ? translationHandler('form.trim.error') : undefined
            }
            appearance={isTrimInvalid ? 'error' : 'default'}
            value={form.trim ?? ''}
            disabled={!form.model || !trims.length}
          />
        </StyledSelectContainer>
      ) : (
        <TextInput
          label={translationHandler('form.tab.vin')}
          placeholder={translationHandler('form.vin.placeholder')}
          required
          value={vin}
          onChange={handleVinChange}
          onBlur={() => {
            updateStoreByFormInput({
              ...form,
              vin,
              modelYear: undefined,
              model: undefined,
              trim: undefined,
            });
          }}
          appearance={vinAppearance}
          message={vinErrorMessage}
        />
      )}
    </StyledContainer>
  );
};
